<template>
    <div class="h-100">
        <ExportChartButton v-if="!settings.tiny" :vega="vega" :series="series"></ExportChartButton>
        <div ref="vegaContainer" class="viz linechart h-100"></div>
        <div v-if="isLoading" class="loading-container">
            <Spinner class="spinner" />
        </div>
    </div>
</template>

<script lang="ts">
import _ from "lodash";
import { isMobile } from "@/utilities";
import Vue, { PropType } from "vue";
import { default as vegaEmbed, VisualizationSpec } from "vega-embed";

import { TimeRange } from "../common";
import { SeriesData, TimeZoom } from "../viz";
import { ChartSettings } from "./SpecFactory";
import chartStyles from "./chartStyles";
import { TimeSeriesSpecFactory } from "./TimeSeriesSpecFactory";
import Spinner from "@/views/shared/Spinner.vue";
import ExportChartButton from "@/views/viz/vega/ExportChartButton.vue";
import Config from "@/secrets";

type DragTimeSignal = [number, number] | null;

function roundForDisplay(value: number): number {
    for (let i = 1; i < 6; ++i) {
        const factor = Math.pow(10, i);
        const rounded = Math.round(value * factor) / factor;
        if (rounded !== 0) {
            return rounded;
        }
    }
    return value;
}

export default Vue.extend({
    name: "LineChart",
    components: {
        Spinner,
        ExportChartButton,
    },
    props: {
        series: {
            type: Array as PropType<SeriesData[]>,
            required: true,
        },
        settings: {
            type: Object as PropType<ChartSettings>,
            default: () => {
                if (isMobile()) {
                    return new ChartSettings(
                        TimeRange.eternity,
                        undefined,
                        { w: 0, h: 0 },
                        true,
                        false,
                        true,
                        Config.enableVegaMobileFeatures
                    );
                }
                const estimated = { w: window.innerWidth, h: window.innerHeight };
                return new ChartSettings(
                    TimeRange.eternity,
                    estimated,
                    { w: 0, h: 0 },
                    true,
                    false,
                    false,
                    Config.enableVegaMobileFeatures
                );
            },
        },
    },
    data(): {
        vega: unknown | undefined;
        isLoading: boolean;
    } {
        return {
            vega: undefined,
            isLoading: false,
        };
    },
    async mounted(): Promise<void> {
        await this.refresh();
    },
    watch: {
        async series(): Promise<void> {
            await this.refresh();
        },
    },
    methods: {
        async refresh(): Promise<void> {
            this.isLoading = true;

            if (this.series.length == 0) {
                this.isLoading = false;
                return;
            }

            const brushable = !this.settings.mobile;
            const draggable = !brushable;
            const factory = new TimeSeriesSpecFactory(this.series, this.settings, brushable, draggable);

            const spec = factory.create();
            const vegaContainer = this.$refs.vegaContainer as HTMLElement;
            const vegaInfo = await vegaEmbed(vegaContainer as HTMLElement, spec as VisualizationSpec, {
                renderer: "svg",
                tooltip: {
                    offsetX: -50,
                    offsetY: 50,
                    formatTooltip: (tooltip, sanitize) => {
                        const roundedValue = roundForDisplay(tooltip.value);
                        const withUoM = [roundedValue, tooltip.unitOfMeasure || " "].join(" ");
                        return `<h3><span class="tooltip-color" style="color: ${sanitize(this.getTooltipColor(tooltip.name))};">■</span>
                                        ${sanitize(tooltip.title)}</h3>
                                        <p class="value">${sanitize(withUoM)}</p>
                                        <p class="time">${sanitize(tooltip.time)}</p>`;
                    },
                },
                downloadFileName: this.getFileName(this.series),
                actions: false,
                scaleFactor: 2,
                padding: this.settings.tiny ? undefined : this.settings.mobile ? { left: 0, right: 10 } : { left: 10, right: 50 },
            });

            this.vega = vegaInfo;

            // Replace vega-embed save as icon with custom button
            if (!this.settings.tiny) {
                const saveButtons = document.querySelectorAll("summary");

                saveButtons.forEach((button) => {
                    if (button.querySelectorAll("span").length === 0) {
                        const svg = button.querySelector("svg");
                        if (svg) {
                            svg.setAttribute("viewBox", "0 0 20 20");
                            svg.innerHTML =
                                '<g id="icon_SaveAs" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round">' +
                                '<line x1="7.96030045" y1="1" x2="7.96030045" y2="11" id="Path-2" stroke="#2C3E50" stroke-width="1.5" stroke-linejoin="round"></line>' +
                                '<polyline id="Path-9" stroke="#2C3E50" stroke-width="1.5" stroke-linejoin="bevel" points="12.8961983 6.50366211 8.05585126 11 2.92245537 6.50366211"></polyline>' +
                                '<polyline id="Path-10" stroke="#2C3E50" stroke-width="1.5" stroke-linejoin="round" points="1 12.5363846 1 16.5 15.1181831 16.5 15.1181831 12.5363846"></polyline>' +
                                "</g>";
                            const saveLabel = document.createElement("span");
                            saveLabel.setAttribute("class", "save-label");
                            saveLabel.innerHTML = this.$tc("dataView.saveAs");
                            button.appendChild(saveLabel);
                        }
                    }
                });
            }

            if (!this.settings.tiny) {
                if (brushable) {
                    let scrubbed = [];

                    vegaInfo.view.addSignalListener("brush", (_, value) => {
                        scrubbed = value.time;
                    });

                    vegaInfo.view.addEventListener("mouseup", () => {
                        if (scrubbed.length == 2) {
                            this.$emit("time-zoomed", new TimeZoom(null, new TimeRange(scrubbed[0], scrubbed[1])));
                        }
                    });

                    // Watch for brush drag outside the window
                    vegaInfo.view.addEventListener("mousedown", (_e) => {
                        window.addEventListener("mouseup", (e) => {
                            if (e.target instanceof Element) {
                                if (scrubbed.length == 2 && e.target && e.target.nodeName !== "path") {
                                    this.$emit("time-zoomed", new TimeZoom(null, new TimeRange(scrubbed[0], scrubbed[1])));
                                }
                            }
                        });
                    });
                } else {
                    /*
                    vegaInfo.view.addSignalListener("down", async (_, value: DragTimeSignal) => {
                        console.log("down", value);
                    });

                    vegaInfo.view.addSignalListener("xcur", async (_, value: DragTimeSignal) => {
                        console.log("xcur", value);
                    });

                    vegaInfo.view.addSignalListener("drag_delta", async (_, value: DragTimeSignal) => {
                        console.log("delta", value);
                    });

                    vegaInfo.view.addSignalListener("chart_clip_size", async (_, value) => {
                        console.log("chart_clip_size", value);
                    });
                    */

                    vegaInfo.view.addSignalListener("visible_times_calc", async (_, value: DragTimeSignal) => {
                        if (value) {
                            this.$emit("time-dragged", new TimeZoom(null, new TimeRange(value[0], value[1])));
                        }
                    });

                    vegaInfo.view.addSignalListener("drag_time", async (_, value: DragTimeSignal) => {
                        if (value) {
                            this.$emit("time-zoomed", new TimeZoom(null, new TimeRange(value[0], value[1])));
                        }
                    });
                }
            }

            console.log("viz: vega:ready", {
                state: vegaInfo.view.getState(),
                // layouts: vegaInfo.view.data("all_layouts"),
            });

            this.isLoading = false;
        },
        getTooltipColor(name: string): string {
            if (name === "LEFT") {
                return chartStyles.primaryLine.stroke;
            }
            if (name === "RIGHT") {
                return chartStyles.secondaryLine.stroke;
            } else {
                return "#ccc";
            }
        },
        getFileName(series: { vizInfo: { station: { name: string }; name: string } }[]): string {
            const stationGroups: Record<string, string[]> = series.reduce((acc, item) => {
                const stationName = item.vizInfo.station.name;
                const sensorName = item.vizInfo.name;

                if (!acc[stationName]) {
                    acc[stationName] = [];
                }
                acc[stationName].push(sensorName);
                return acc;
            }, {} as Record<string, string[]>);

            const entries = Object.entries(stationGroups);

            if (entries.length === 1) {
                const [stationName, sensorNames] = entries[0];
                if (sensorNames.length === 1) {
                    return `${stationName}-${sensorNames[0]}`;
                } else {
                    return `${stationName} ${sensorNames.join(" - ")}`;
                }
            } else if (entries.length === 2) {
                const [[station1Name, sensor1Names], [station2Name, sensor2Names]] = entries;
                if (sensor1Names.length === 1 && sensor2Names.length === 1) {
                    return `${station1Name}-${sensor1Names[0]}_${station2Name}-${sensor2Names[0]}`;
                }
            }

            const fileName: string = entries
                .map(([stationName, sensorNames]) => {
                    return `${stationName}-${sensorNames.join("-")}`;
                })
                .join(" ");

            return fileName;
        },
    },
});
</script>

<style lang="scss">
@use "src/scss/mixins";
@use "src/scss/variables";

.viz {
    width: 100%;
}

.vega-embed summary {
    border-radius: 0px !important;
    height: 1em;
    display: flex;
    align-items: center;
    margin-right: 3.2em !important;
    opacity: 1 !important;

    @include mixins.bp-down(variables.$sm) {
        bottom: -225px;
        top: unset !important;
        left: 50%;
        transform: translateX(-50%);
        width: 80px;

        span {
            font-size: 14px;
            font-family: variables.$font-family-bold;
        }
    }
}
.vega-embed summary svg {
    width: 16px !important;
    height: 16px !important;
    display: inline-block;

    @include mixins.bp-down(variables.$sm) {
        width: 20px !important;
        height: 20px !important;
    }
}
.vega-embed .vega-actions {
    right: 3em !important;

    @include mixins.bp-down(variables.$sm) {
        bottom: -225px;
        top: unset !important;
        right: 50% !important;
    }
}

.vega-embed.has-actions {
    @include mixins.bp-down(variables.$sm) {
        padding-right: 0 !important;
    }
}
.save-label {
    font-size: 12px;
    margin-left: 5px;
}
</style>
